// Strategy editor component (LAB) — the body of a strategy sub-box in the Build
// side panel. It renders ONE tab at a time; which tab is shown is driven by the
// icon strip in the sub-box header (strategy-view). A tab is either a why (its
// human dials + role), "manage" (direction/stop/target/size), or "add" (the why
// picker). Operates on a plain StoredStrategy and calls onChange with an updated
// copy; the panel persists that to the shared book and the chart picks it up.
// SPATIAL bits (geometric why level, stop/target) are placed by dragging on the
// chart, so here they only show read-only values.
import { el } from "./dom.js";
import { WHY_DEFS, MANAGE_STOP_COLOR, MANAGE_TARGET_COLOR, whyRuleText, type WhyKind, type WhyState, type TradeDir, type ManageMode } from "./studio-model.js";
import type { StoredStrategy } from "./studio-store.js";

/** A tab key: a why id or the manage panel. Whys are toggled on/off from the
 * icon palette in the header (strategy-view); the editor just shows the selected
 * one's settings. */
export type EditorTab = WhyKind | "manage";

export interface EditorCallbacks {
  selected: EditorTab; // which tab to show (owned by strategy-view)
  onSelect: (tab: EditorTab) => void; // ask the view to switch tab (rebuilds header + body)
  onChange: (next: StoredStrategy) => void; // any edit → persist + sync
  onDeploy?: (s: StoredStrategy) => void;
  onRemove?: (s: StoredStrategy) => void; // delete this strategy
}

const clone = (s: StoredStrategy): StoredStrategy => JSON.parse(JSON.stringify(s));

/** A WhyState-shaped view of a stored why, so studio-model helpers (whyRuleText)
 * work unchanged. */
function whyState(id: WhyKind, price: number | undefined, tol: number | undefined, params: Record<string, number> | undefined): WhyState {
  const def = WHY_DEFS.find((d) => d.id === id)!;
  return { def, price, tol, params };
}

export function renderStrategyEditor(strat: StoredStrategy, cb: EditorCallbacks): HTMLElement {
  const root = el("div", { style: { padding: "2px 0" } });
  const emit = () => cb.onChange(clone(strat));
  // Same-tab restructure (role/direction/mode toggle) → repaint the body in place.
  // Slider drags call emit() only, so they never rebuild mid-drag.
  const restructure = () => { emit(); paint(); };

  /** Resolve the tab to show: fall back to the first active why, or manage. */
  const resolveTab = (): EditorTab => {
    const ids = strat.whys.map((w) => w.id);
    if (cb.selected === "manage") return "manage";
    if (ids.includes(cb.selected as WhyKind)) return cb.selected;
    return ids.length ? ids[0]! : "manage";
  };

  const paint = () => {
    root.replaceChildren();
    if (!strat.whys.length && cb.selected !== "manage") { paintEmpty(); return; }
    const tab = resolveTab();
    if (tab === "manage") paintManage();
    else paintWhy(tab);
  };

  // --- Empty: no trade type toggled on yet ---
  function paintEmpty() {
    root.appendChild(el("div", { text: "Hover the card and click a trade type to switch it on.", style: { color: "var(--text-secondary)", fontSize: "12px", lineHeight: "1.4", padding: "6px 2px" } }));
  }

  // --- Tab: one why's settings ---
  function paintWhy(id: WhyKind) {
    const w = strat.whys.find((x) => x.id === id);
    const def = WHY_DEFS.find((d) => d.id === id);
    if (!w || !def) { paintEmpty(); return; }
    // No title text — the highlighted icon in the header already says which type
    // this is. Straight to its controls.
    // Role: TRIGGER fires the entry; FILTER just has to hold at that moment.
    const roleWrap = el("div", { style: { display: "flex", gap: "5px", margin: "0 0 4px" } });
    for (const r of ["trigger", "filter"] as const) {
      const on = (w.role ?? "trigger") === r;
      const rb = el("button", { text: r === "trigger" ? "Trigger" : "Filter", attrs: { title: r === "trigger" ? "Fires the entry" : "Must be true when the trigger fires" }, style: { padding: "3px 10px", borderRadius: "999px", cursor: "pointer", fontSize: "10.5px", fontWeight: on ? "700" : "400", border: "1px solid " + (on ? def.color : "var(--border,#e4e7ec)"), background: on ? def.color : "#fff", color: on ? "#fff" : "var(--text-secondary)" } });
      rb.onclick = () => { w.role = r; restructure(); };
      roleWrap.appendChild(rb);
    }
    root.appendChild(roleWrap);
    if (def.shape === "geometric") {
      root.appendChild(el("div", { text: w.price != null ? `Level ${w.price.toFixed(1)} — drag on the chart` : "Placing on the chart…", style: { fontSize: "11px", color: def.color } }));
    }
    // Sliders live ONLY on the chart's under-stats strip now (one tuning surface,
    // so the two windows don't fight over focus). The side editor just points there.
    if (def.dials?.length) root.appendChild(el("div", { text: "Tune this on the chart — open the strategy palette (sliders icon by the ⓘ) and adjust it under the stats bar.", style: { fontSize: "10.5px", color: "var(--text-secondary)", lineHeight: "1.4", margin: "6px 0 0" } }));
  }

  // --- Tab: manage + size ---
  function paintManage() {
    const m = strat.manage;
    const mgt = el("div", { style: { padding: "2px 0" } });
    mgt.appendChild(el("div", { text: "Manage the trade", style: { fontWeight: "700", marginBottom: "5px", fontSize: "12.5px" } }));
    const toggleRow = (opts: Array<[string, boolean, () => void]>) => {
      const wrap = el("div", { style: { display: "flex", gap: "6px", marginBottom: "6px" } });
      for (const [label, on, onClick] of opts) {
        const b = el("button", { text: label, style: { flex: "1", padding: "5px 0", borderRadius: "7px", cursor: "pointer", fontSize: "12px", fontWeight: on ? "700" : "400", border: "1px solid " + (on ? "var(--accent-blue,#2f6df6)" : "var(--border,#e4e7ec)"), background: on ? "var(--accent-blue,#2f6df6)" : "#fff", color: on ? "#fff" : "var(--text-primary,#1d1d1f)" } });
        b.onclick = onClick;
        wrap.appendChild(b);
      }
      return wrap;
    };
    mgt.appendChild(toggleRow([
      ["Long", m.dir === "long", () => { m.dir = "long" as TradeDir; restructure(); }],
      ["Short", m.dir === "short", () => { m.dir = "short" as TradeDir; restructure(); }],
    ]));
    mgt.appendChild(toggleRow([
      ["Levels", m.mode === "levels", () => { m.mode = "levels" as ManageMode; restructure(); }],
      ["Structure", m.mode === "structure", () => { m.mode = "structure" as ManageMode; restructure(); }],
    ]));
    if (m.mode === "levels") {
      const lvl = (label: string, v: number | undefined, color: string) =>
        el("div", { style: { display: "flex", alignItems: "center", gap: "6px", margin: "3px 0", fontSize: "12px" } }, [
          el("span", { style: { width: "9px", height: "9px", borderRadius: "2px", background: color, flex: "none" } }),
          el("span", { text: v != null ? `${label} ${v.toFixed(1)}` : `${label} — placing…`, style: { flex: "1" } }),
          el("span", { text: "drag on chart", style: { fontSize: "10px", color: "var(--text-secondary)" } }),
        ]);
      mgt.appendChild(lvl("Stop", m.stop, MANAGE_STOP_COLOR));
      mgt.appendChild(lvl("Target", m.target, MANAGE_TARGET_COLOR));
    } else {
      mgt.appendChild(el("div", { text: "The stop sits at each entry's recent swing; the target is set from there.", style: { fontSize: "10.5px", color: "var(--text-secondary)", lineHeight: "1.35", margin: "2px 0 5px" } }));
      const human = (left: string, right: string, get: () => number, set: (v: number) => void, min: number, max: number, step: number) => {
        mgt.appendChild(el("div", { style: { display: "flex", justifyContent: "space-between", fontSize: "10.5px", color: "var(--text-secondary)", margin: "6px 0 1px" } }, [el("span", { text: left }), el("span", { text: right })]));
        const sl = el("input", { attrs: { type: "range", min: String(min), max: String(max), step: String(step), value: String(get()) }, style: { width: "100%", accentColor: MANAGE_TARGET_COLOR } }) as HTMLInputElement;
        sl.oninput = () => { set(parseFloat(sl.value)); emit(); };
        mgt.appendChild(sl);
      };
      human("Tight stop", "Loose stop", () => m.swingLB ?? 10, (v) => (m.swingLB = v), 3, 30, 1);
      human("Quick profit", "Let it run", () => m.rMult ?? 2, (v) => (m.rMult = v), 0.5, 5, 0.5);
    }
    const sizeVal = el("span", { text: `${strat.riskPct.toFixed(2)}%`, style: { fontWeight: "600" } });
    mgt.appendChild(el("div", { style: { display: "flex", justifyContent: "space-between", fontSize: "11px", margin: "8px 0 2px" } }, [el("span", { text: "Risk per trade", style: { color: "var(--text-secondary)" } }), sizeVal]));
    const sizeSl = el("input", { attrs: { type: "range", min: "0.25", max: "3", step: "0.25", value: String(strat.riskPct) }, style: { width: "100%" } }) as HTMLInputElement;
    sizeSl.oninput = () => { strat.riskPct = parseFloat(sizeSl.value); sizeVal.textContent = `${strat.riskPct.toFixed(2)}%`; emit(); };
    mgt.appendChild(sizeSl);
    root.appendChild(mgt);
  }

  paint();
  return root;
}
